import { type AlternativeSuggestion, SCREEN_LABELS } from '../lib/types'

interface Props {
  alternatives: AlternativeSuggestion[]
}

// Wording stays strictly on the data side of the regulatory boundary:
// we describe lower-exposure funds with similar characteristics, never
// "sell X / buy Y".
export default function AlternativesPanel({ alternatives }: Props) {
  if (alternatives.length === 0) return null

  // Group by the fund the user currently holds.
  const byFund = new Map<string, AlternativeSuggestion[]>()
  for (const alt of alternatives) {
    const key = alt.replaces_ticker ?? alt.replaces_fund_isin
    const list = byFund.get(key)
    if (list) list.push(alt)
    else byFund.set(key, [alt])
  }

  return (
    <div className="alternatives">
      <h3>Lower-exposure alternatives</h3>
      <p className="muted">
        Funds with lower exposure to your selected screens and broadly similar mandates, shown with
        their expense ratios. This is informational, not a recommendation.
      </p>

      {[...byFund.entries()].map(([fund, alts]) => (
        <div className="alt-group" key={fund}>
          <h4>Lower-exposure funds comparable to {fund}</h4>
          <ul>
            {alts.map((alt, i) => (
              <li key={`${alt.alternative_fund_isin}-${alt.screen}-${i}`}>
                <div className="alt-head">
                  <strong>{alt.alternative_ticker ?? alt.alternative_fund_isin}</strong>
                  {alt.alternative_name ? <span> — {alt.alternative_name}</span> : null}
                  <span className="alt-tag">{SCREEN_LABELS[alt.screen]}</span>
                </div>
                <div className="alt-meta">
                  {alt.expense_ratio != null && (
                    <span>Expense ratio: {alt.expense_ratio.toFixed(2)}%</span>
                  )}
                  {alt.available_at?.length > 0 && (
                    <span>Available at: {alt.available_at.join(', ')}</span>
                  )}
                </div>
                {alt.tracking_notes && <p className="alt-notes">{alt.tracking_notes}</p>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
